import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usersApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AccountLink, UUID } from '@/types/domain';

interface Props {
  /** Whose account this is, in words: "this student", "this guardian". */
  subject: string;
  /** The role the linked user needs for the portal to show them anything. */
  role: 'STUDENT' | 'PARENT';
  queryKey: QueryKey;
  load: () => Promise<AccountLink>;
  link: (userId: UUID) => Promise<AccountLink>;
  unlink: () => Promise<AccountLink>;
}

/**
 * The sign-in account behind a student or guardian.
 *
 * Linking only ties the person to an existing user; it grants nothing. The
 * portal role is assigned on the user itself, which is why the hint is always
 * on screen — a linked account without the role sees an empty portal and the
 * office would otherwise be left guessing why.
 */
export function PortalAccountPanel({ subject, role, queryKey, load, link, unlink }: Props) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [picking, setPicking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const account = useQuery({ queryKey, queryFn: load });

  const linkAccount = useMutation({
    mutationFn: link,
    onSuccess: (updated) => {
      toast.success(`Linked to ${updated.userEmail ?? 'the account'}`);
      queryClient.setQueryData(queryKey, updated);
      setPicking(false);
    },
    onError: (error) => handleApiError(error),
  });

  const unlinkAccount = useMutation({
    mutationFn: unlink,
    onSuccess: (updated) => {
      toast.success('Portal account unlinked');
      queryClient.setQueryData(queryKey, updated);
      setUnlinking(false);
    },
    onError: (error) => {
      handleApiError(error);
      setUnlinking(false);
    },
  });

  const canManage = can(PERMISSIONS.USER_UPDATE);
  const linked = account.data?.userId !== undefined;

  return (
    <Card>
      <CardHeader
        title="Portal account"
        description={`The user who signs in as ${subject}.`}
        action={
          canManage && account.data !== undefined ? (
            linked ? (
              <Button size="sm" variant="ghost" onClick={() => setUnlinking(true)}>
                Unlink
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
                Link account
              </Button>
            )
          ) : undefined
        }
      />

      {account.isPending ? (
        <LoadingState label="Loading account" />
      ) : account.error !== null ? (
        <ErrorState error={account.error} onRetry={() => void account.refetch()} />
      ) : (
        <div className="space-y-3 px-5 py-4">
          {linked ? (
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{account.data.userName ?? '—'}</p>
                <p className="truncate text-xs text-muted">{account.data.userEmail ?? '—'}</p>
              </div>
              {account.data.userActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
            </div>
          ) : (
            <p className="text-sm text-subtle">No portal account linked.</p>
          )}
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-muted">
            Linking grants no access by itself — assign the {role} role on the user as well.
          </p>
        </div>
      )}

      {picking && (
        <UserPickerModal
          role={role}
          saving={linkAccount.isPending}
          onClose={() => setPicking(false)}
          onPick={(userId) => linkAccount.mutate(userId)}
        />
      )}

      <ConfirmDialog
        open={unlinking}
        title="Unlink portal account"
        message={`Unlink ${account.data?.userEmail ?? 'this account'} from ${subject}? The user is kept, but will no longer see ${subject}'s records in the portal.`}
        confirmLabel="Unlink"
        destructive
        loading={unlinkAccount.isPending}
        onConfirm={() => unlinkAccount.mutate()}
        onCancel={() => setUnlinking(false)}
      />
    </Card>
  );
}

function UserPickerModal({ role, saving, onClose, onPick }: {
  role: Props['role'];
  saving: boolean;
  onClose: () => void;
  onPick: (userId: UUID) => void;
}) {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const canSearch = can(PERMISSIONS.USER_VIEW);
  const params = { search: debouncedSearch, page: 0, size: 20 };
  const candidates = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
    enabled: canSearch,
  });

  return (
    <Modal
      open
      title="Link a portal account"
      description="Choose an existing user. Create the user first from the Users screen if they have none."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => onPick(userId)} loading={saving} disabled={userId === ''}>
            Link account
          </Button>
        </>
      }
    >
      {!canSearch ? (
        <p className="text-sm text-muted">You need permission to view users to pick an account.</p>
      ) : (
        <div className="space-y-4">
          <TextField
            label="Find a user"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or e-mail…"
          />
          <SelectField
            label="User"
            required
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            hint={
              candidates.error !== null
                ? 'Could not load users.'
                : candidates.data?.data.length === 0
                  ? 'No matches.'
                  : `The user should hold the ${role} role.`
            }
          >
            <option value="">{candidates.isFetching ? 'Loading…' : 'Select a user…'}</option>
            {candidates.data?.data.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName} · {user.email}
              </option>
            ))}
          </SelectField>
        </div>
      )}
    </Modal>
  );
}
